import {
    getTimeframeDescriptiveText,
    getTimeframeLabel,
    isCustomTimeframe,
    timeframeNames,
    UnresolvedTimeframe
} from '@squaredup/timeframes';
import { groupBy, isEqual } from 'lodash';
import { useState } from 'react';
import { CustomTimeframeForm, CustomTimeframeHeader } from './CustomTimeframeForm';
import TimeframeMenu from './TimeframeMenu';
import { isActiveTimeframe } from './timeframeFormUtils';

type DashboardTimeframeChooserProps = {
    currentTimeframe: UnresolvedTimeframe;
    defaultTimeframe?: UnresolvedTimeframe;
    disabled?: boolean;
    canChangeTimeframe?: boolean;
    onCurrentTimeframeChange: (timeframe: UnresolvedTimeframe) => void;
    onDefaultTimeframeChange?: (timeframe: UnresolvedTimeframe) => void;
};

const DashboardTimeframeChooser: React.FC<DashboardTimeframeChooserProps> = ({
    currentTimeframe,
    defaultTimeframe,
    disabled = false,
    canChangeTimeframe,
    onCurrentTimeframeChange,
    onDefaultTimeframeChange
}) => {
    const groupedTimeframes = groupBy(timeframeNames, 'group');
    const timeframeLabel = getTimeframeLabel(currentTimeframe);
    const [isOpen, setOpen] = useState(false);
    const [customFormOpen, setCustomFormOpen] = useState(false);
    const timeframeDescriptiveText = getTimeframeDescriptiveText(currentTimeframe);

    const isCurrentTimeframeCustom = isCustomTimeframe(currentTimeframe);
    const isDefaultTimeframeCustom = isCustomTimeframe(defaultTimeframe);
    const isCurrentTimeframeDefault = isEqual(defaultTimeframe, currentTimeframe);
    /**
     * Whether to show pin on `Custom` option, only if custom timeframe is the default.
     * If the user has changed to a new custom timeframe, we don't want to show the pin.
     */
    const isCurrentCustomTimeframeDefault =
        isDefaultTimeframeCustom && (!isCurrentTimeframeCustom || isCurrentTimeframeDefault);

    // Always show the custom form if the current timeframe is a custom timeframe
    const showCustomForm = customFormOpen || isCurrentTimeframeCustom;

    return (
        <TimeframeMenu
            open={isOpen}
            onOpenChange={() => {
                setOpen(!isOpen);
                setCustomFormOpen(false);
            }}
        >
            <TimeframeMenu.Trigger
                data-testid='dashboardTimeframeChooser'
                title={timeframeDescriptiveText}
                disabled={disabled}
            >
                {timeframeLabel}
            </TimeframeMenu.Trigger>
            <TimeframeMenu.MenuContent>
                <TimeframeMenu.FiltersMenu>
                    {Object.entries(groupedTimeframes).map(([timeframeGroup, timeframes]) => {
                        return (
                            <TimeframeMenu.Group key={timeframeGroup}>
                                {timeframes.map((timeframeInstance) => {
                                    const { name: timeframe, label, getResolvedTimeRange } = timeframeInstance;
                                    const isActive = !showCustomForm && isActiveTimeframe(timeframe, currentTimeframe);
                                    const isDefault = isActiveTimeframe(timeframe, defaultTimeframe);
                                    return (
                                        <TimeframeMenu.FilterButton
                                            key={timeframe}
                                            onClick={() => {
                                                onCurrentTimeframeChange(timeframe);
                                                setOpen(false);
                                            }}
                                            isActive={isActive}
                                        >
                                            <TimeframeMenu.TimeframeLabel data-testid={timeframe}>
                                                {label}
                                            </TimeframeMenu.TimeframeLabel>

                                            <TimeframeMenu.TimeframePin
                                                disabled={!canChangeTimeframe || !onDefaultTimeframeChange}
                                                onClick={() => onDefaultTimeframeChange?.(timeframe)}
                                                isActive={isDefault}
                                            />

                                            <TimeframeMenu.TimeframePreview
                                                data-testid={`resolvedTimeframe-${timeframe}`}
                                            >
                                                {getResolvedTimeRange()}
                                            </TimeframeMenu.TimeframePreview>
                                        </TimeframeMenu.FilterButton>
                                    );
                                })}
                            </TimeframeMenu.Group>
                        );
                    })}
                    <TimeframeMenu.FilterButton
                        key='custom'
                        isActive={showCustomForm}
                        onClick={() => {
                            setCustomFormOpen(!customFormOpen);
                        }}
                        data-testid='customTimeframe'
                    >
                        <TimeframeMenu.TimeframeLabel>Custom...</TimeframeMenu.TimeframeLabel>
                        <TimeframeMenu.TimeframePin
                            disabled={!isCurrentCustomTimeframeDefault}
                            isActive={isCurrentCustomTimeframeDefault}
                            title='Default timeframe'
                        />
                    </TimeframeMenu.FilterButton>
                </TimeframeMenu.FiltersMenu>
                {showCustomForm && (
                    <TimeframeMenu.AdvancedMenu>
                        <CustomTimeframeHeader />
                        <CustomTimeframeForm
                            onChange={onCurrentTimeframeChange}
                            currentTimeframe={isCurrentCustomTimeframeDefault ? defaultTimeframe : currentTimeframe}
                            isCurrentDefaultTimeframe={isCurrentCustomTimeframeDefault}
                            onDefaultTimeframeChange={canChangeTimeframe ? onDefaultTimeframeChange : undefined}
                            showCalendarSettings
                        />
                    </TimeframeMenu.AdvancedMenu>
                )}
            </TimeframeMenu.MenuContent>
        </TimeframeMenu>
    );
};

export default DashboardTimeframeChooser;
